import type { DayPillarInterpretation } from './sajuInterpretation';

const INTERPRETATIONS_EN: Record<string, DayPillarInterpretation> = {
  // ────── 甲(Jia/Yang Wood) ──────
  '甲子': {
    stem: '甲', branch: '子',
    title: 'Nurtured Wood — Strong outside, warm within',
    summary: 'The energy of a great tree holding water. You appear confident and loyal on the outside, but are deeply sensitive and caring on the inside.',
    personality: ['Loyal and trustworthy by nature', 'Tough exterior but soft heart', 'Natural leader who feels lonely in solitude'],
    strengths: ['Takes full responsibility for committed tasks', 'Takes great care of the people around you', 'Stays calm and composed under pressure'],
    weaknesses: ['Can be stubborn with little flexibility', 'Reluctance to show vulnerability may cause misunderstandings', 'Prone to stress from perfectionism'],
    career: ['Management & leadership roles', 'Healthcare, education, counseling', 'Government or large corporations'],
    relationship: 'Happiest with a caring, empathetic partner. Practice expressing your true feelings more openly.',
    wealth: 'Prefers stable income. Saving suits you better than speculative investing.',
    health: 'Watch your liver and kidneys. Find healthy outlets for stress.',
    advice: "It's okay to show your vulnerable side sometimes. The people around you already trust you.",
  },
  '甲寅': {
    stem: '甲', branch: '寅',
    title: 'Prosperous Wood — Bamboo that never stops growing',
    summary: 'A tree brimming with spring energy. You have a powerful drive to grow and improve continuously.',
    personality: ['Proactive and challenge-oriented', 'Strong desire to learn and develop', 'Always tries to stay one step ahead'],
    strengths: ['Learns new things quickly', 'Turns crises into opportunities', 'Strong perseverance and endurance'],
    weaknesses: ['Impatience can lead to errors', 'Stressed by comparing yourself to others', 'May scatter energy across too many paths'],
    career: ['Entrepreneurship & startups', 'Education, coaching, speaking', 'Planning, marketing, sales'],
    relationship: 'Best with a partner you can grow with together. Overly dependent relationships feel suffocating.',
    wealth: 'Income tends to grow fast, but so does spending. Build a savings habit.',
    health: 'Watch your neck, shoulders, and joints. Be careful when driving.',
    advice: 'Growth is a marathon. It\'s okay to rest. You\'re already doing enough.',
  },
  '甲辰': {
    stem: '甲', branch: '辰',
    title: 'Dragon Wood — Soaring ambition above the clouds',
    summary: 'A tree standing on a dragon\'s back, full of grand visions. You have high ideals and a strong drive to transcend reality.',
    personality: ['High ideals and big dreams', 'Strong endurance and determination', 'Values appearances and reputation'],
    strengths: ['Never gives up on long-term goals', 'Excellent organizational skills and drive', 'Broad-minded with natural leadership'],
    weaknesses: ['Conflicts between reality and ideals', 'Stubbornness can cause friction', 'Can become overconfident at times'],
    career: ['Administration, planning, politics', 'Construction, real estate, development', 'Senior management or public institutions'],
    relationship: 'Needs a partner who appreciates you. Be careful not to overpower others.',
    wealth: 'Steady financial planning suits you better than bold ventures.',
    health: 'Watch your digestive system and skin.',
    advice: 'While painting the big picture is great, each step you take matters too.',
  },
  '甲午': {
    stem: '甲', branch: '午',
    title: 'Phoenix Wood — A tree reaching straight for the sun',
    summary: 'A tree stretching boldly toward the sun. Progressive, passionate, and socially vibrant.',
    personality: ['Bright, positive energy', 'Passionate and expressive', 'Strong desire for recognition and competition'],
    strengths: ['Outstanding drive and action', 'Energizes and motivates those around you', 'Full of creative ideas'],
    weaknesses: ['Impulsive decision-making', 'Emotional fluctuations', 'Needs time to find center'],
    career: ['Entertainment, media, PR', 'Marketing, sales, speaking', 'Startups and entrepreneurship'],
    relationship: 'Draws to bright, energetic partners.',
    wealth: 'Good at seizing big opportunities but beware overspending.',
    health: 'Watch your heart, blood pressure, and vision.',
    advice: 'Passion is your greatest weapon. But choose a direction and focus your energy.',
  },
  '甲申': {
    stem: '甲', branch: '申',
    title: 'Carved Wood — Raw timber shaped by the axe',
    summary: 'Raw wood being refined into something useful. You become stronger and more resilient through trials.',
    personality: ['Practical and realistic', 'Strong willpower and persistence', 'Sharp, analytical judgment'],
    strengths: ['Inner strength to overcome adversity', 'Logical and analytical thinking', 'Possesses long-term vision'],
    weaknesses: ['Sharpness may create friction with others', 'Poor at expressing emotions', 'Tends to make unilateral decisions'],
    career: ['IT, engineering, manufacturing', 'Law, military, police', 'Consulting and analysis'],
    relationship: 'Wants a trustworthy partner. Practice expressing your feelings more actively.',
    wealth: 'Builds steady wealth through practical investment and saving.',
    health: 'Watch your respiratory system and large intestine.',
    advice: 'Being refined is beautiful. Express your gratitude to those around you.',
  },
  '甲戌': {
    stem: '甲', branch: '戌',
    title: 'Deep Autumn Wood — A tree gaining depth in autumn',
    summary: 'A tree that deepens as autumn arrives. Thoughtful and dependable with a rich inner presence.',
    personality: ['Careful and deep thinker', 'Strong loyalty and responsibility', 'Conservative but warm-hearted'],
    strengths: ['High trustworthiness and loyalty', 'Achieves results through steady effort', 'Builds deep, meaningful relationships'],
    weaknesses: ['Slow to adapt to change', 'Can appear stubborn', 'Tends to hide emotions'],
    career: ['Finance, insurance, real estate', 'Education and research', 'Public companies and stable organizations'],
    relationship: 'Values relationships that deepen over time.',
    wealth: 'Builds assets through consistent saving and safe investments.',
    health: 'Watch your stomach and musculoskeletal system.',
    advice: 'Your depth and trustworthiness are precious assets. Don\'t fear change either.',
  },

  // ────── 乙(Yi/Yin Wood) ──────
  '乙丑': {
    stem: '乙', branch: '丑',
    title: 'Field Grass — Grass awaiting spring in frozen soil',
    summary: 'The energy of grass waiting for spring under cold ground. With tenacious vitality and patience, you eventually bloom.',
    personality: ['Extremely patient and persistent', 'Introverted yet internally tough', 'Practical and realistic'],
    strengths: ['Life force that endures anything', 'Consistently creates results through perseverance', 'Skilled at saving and economy'],
    weaknesses: ['Can bottle up feelings and explode at once', 'Fears change and challenges', 'Lack of expression can lead to misunderstandings'],
    career: ['Agriculture, food industry, restaurants', 'Accounting, finance, taxation', 'Civil service or stable employment'],
    relationship: 'Forms deep bonds with people you\'ve known for a long time.',
    wealth: 'A saver who builds substantial wealth through frugality.',
    health: 'Watch your digestive organs and immune system.',
    advice: 'Waiting is also a strategy. Your time will surely come.',
  },
  '乙卯': {
    stem: '乙', branch: '卯',
    title: 'Vine Wood — A vine that gracefully climbs upward',
    summary: 'The energy of a vine adapting and growing around its environment. Flexible, sensitive, and values human connections.',
    personality: ['Sensitive and deeply considerate', 'Flexible and adaptive', 'Exceptional artistic sensibility'],
    strengths: ['Excellent at building relationships', 'Handles crises gracefully', 'Strong attention to detail'],
    weaknesses: ['Can be indecisive', 'Overly conscious of others\' opinions', 'May suffer from low self-esteem'],
    career: ['Design, arts, culture', 'Counseling, coordination', 'Service industry'],
    relationship: 'Feels secure with a reliable partner. Express your own opinions more.',
    wealth: 'Consistent accumulation is key. Small, regular investment suits you better than big bets.',
    health: 'Watch your digestive system and skin. Stress often shows up on the skin.',
    advice: 'Your flexibility is a strength. But sometimes you need to draw firm boundaries.',
  },
  '乙巳': {
    stem: '乙', branch: '巳',
    title: 'Flame Grass — Grass that shines beside fire',
    summary: 'Grass that receives light from nearby flames and shines beautifully. Intelligent with quick wit.',
    personality: ['Sharp and intelligent mind', 'Eloquent and sociable', 'Loves change and naturally curious'],
    strengths: ['Quick situational judgment', 'Excellent verbal and persuasive skills', 'Learns new fields quickly'],
    weaknesses: ['Difficulty concentrating on one thing', 'Inconsistency and weak follow-through', 'Words may come before action'],
    career: ['Broadcasting, media, writing', 'Education, speaking, consulting', 'IT and marketing'],
    relationship: 'Best with a partner who connects intellectually.',
    wealth: 'Has the ability to earn money through ideas and wit.',
    health: 'Watch your heart, eyes, and nervous system.',
    advice: 'Develop the power to turn ideas into action.',
  },
  '乙未': {
    stem: '乙', branch: '未',
    title: 'Prairie Grass — Grass freely covering open fields',
    summary: 'Like grass growing freely across vast fields — broad tolerance and a free spirit.',
    personality: ['Broad-minded and free-spirited', 'Artistic and emotional temperament', 'Warm and approachable'],
    strengths: ['Outstanding artistic sensibility', 'Creates a gentle atmosphere around you', 'Deep patience and consideration'],
    weaknesses: ['May lack decisiveness', 'Weak self-assertion can lead to being taken advantage of', 'Emotional fluctuations'],
    career: ['Art, music, fashion', 'Education, volunteering, social work', 'Service and tourism'],
    relationship: 'Wants warm and free-flowing relationships.',
    wealth: 'Tends to spend for emotional satisfaction over stable income.',
    health: 'Watch your digestive system and joints.',
    advice: 'Your broad-heartedness is your greatest charm.',
  },
  '乙酉': {
    stem: '乙', branch: '酉',
    title: 'Jeweled Grass — A flower resting on a jewelry box',
    summary: 'Like a flower placed on fine jewelry — refined and beautiful energy with outstanding aesthetic sense.',
    personality: ['Exceptional aesthetic sense and refinement', 'Delicate and sensitive sensibility', 'Perfectionist tendencies'],
    strengths: ['Outstanding artistic talent', 'Meticulous and accurate work', 'Draws attention with unique charm'],
    weaknesses: ['Too sensitive, easily hurt', 'Strong pride, averse to criticism', 'Can be obsessed with ideals over reality'],
    career: ['Fashion, beauty, design', 'Art, music, film', 'Tax accounting, law'],
    relationship: 'Wants a sophisticated partner who shares your taste.',
    wealth: 'Creates stable income through professional expertise.',
    health: 'Watch your lungs, respiratory system, and skin.',
    advice: 'You don\'t need to be perfect. You\'re already beautiful enough.',
  },
  '乙亥': {
    stem: '乙', branch: '亥',
    title: 'Aquatic Grass — Water plant swaying gently in the depths',
    summary: 'Like aquatic plants swaying in deep water yet firmly rooted — strong intuition and deep emotional sensitivity.',
    personality: ['Strong intuition and rich emotional depth', 'Mysterious charm and deep inner world', 'Free-spirited and open'],
    strengths: ['Quickly reads people\'s emotions', 'Outstanding creativity and imagination', 'Adapts to any environment'],
    weaknesses: ['Emotional volatility', 'Can lose touch with reality', 'Weak sense of boundaries — can be taken advantage of'],
    career: ['Art, literature, psychology'],
    relationship: 'Wants a partner capable of deep emotional exchange.',
    wealth: 'Managing emotional spending helps wealth accumulate.',
    health: 'Watch your kidneys and immune system.',
    advice: 'Channeling your deep sensitivity into art is where you shine.',
  },

  // ────── 丙(Bing/Yang Fire) ──────
  '丙子': {
    stem: '丙', branch: '子',
    title: 'Fire on Ice — A flame blazing in frozen winter',
    summary: 'A flame roaring in the depths of winter — coexisting intense passion and extreme temperament.',
    personality: ['Fierce passion and drive', 'All-in temperament with extremes', 'Strong and striking presence'],
    strengths: ['Energy that breaks through any situation', 'Spreads passion to people around you', 'Stronger spirit of challenge than anyone'],
    weaknesses: ['Impulsive and extreme choices', 'May lack consistency', 'Emotional swings and easy burnout'],
    career: ['Sales, marketing, broadcasting', 'Sports and performance', 'Startups and entrepreneurship'],
    relationship: 'Attracted to passionate people, but emotional swings can destabilize relationships.',
    wealth: 'High potential income but watch impulsive spending.',
    health: 'Watch the balance between your kidneys and heart.',
    advice: 'After burning bright, your body needs rest too.',
  },
  '丙寅': {
    stem: '丙', branch: '寅',
    title: 'Rising Sun — A sun awakening the dawn',
    summary: 'The sun painting the early morning sky red — bright, active, and born to pioneer.',
    personality: ['Vibrant, positive energy', 'Pioneer spirit and leadership', 'Action before words'],
    strengths: ['Opens new paths with a trailblazer\'s spirit', 'Invigorates others with bright energy', 'Strong decisiveness and drive'],
    weaknesses: ['Charges ahead without planning', 'Weak at completing tasks', 'Can lack patience'],
    career: ['Entrepreneurship and business', 'Sports and athletics', 'Exploration and pioneering roles'],
    relationship: 'Creates amazing chemistry with energy-matched partners.',
    wealth: 'Earns fast and invests fast.',
    health: 'Watch your muscles and liver.',
    advice: 'The last step is just as important as the first.',
  },
  '丙辰': {
    stem: '丙', branch: '辰',
    title: 'Sunbeam Through Clouds — Sunlight breaking through overcast skies',
    summary: 'Sunlight pushing through clouds — creative and mysterious energy that overcomes all obstacles.',
    personality: ['Creative and imaginative', 'Mysterious aura with deep inner world', 'Where emotion and reason coexist'],
    strengths: ['Creative problem-solving ability', 'Uncommon insight and intuition', 'Will to break through any obstacle'],
    weaknesses: ['Dreaminess can create gaps with reality', 'Decisions take a long time', 'Struggles with situations requiring patience'],
    career: ['Research, development, planning', 'Arts and philosophy', 'IT and design'],
    relationship: 'Wants a partner with whom intellectual conversation flows naturally.',
    wealth: 'Monetizing ideas can generate significant income.',
    health: 'Watch your digestive system and skin.',
    advice: 'Light shines brightest in the dark. Enjoy the process.',
  },
  '丙午': {
    stem: '丙', branch: '午',
    title: 'Blazing Sun — The midday sun burning bright',
    summary: 'Like the midday sun — hot, bright, and full of energy that lights up everything around you.',
    personality: ['Bright and positive energy', 'Strong leadership and drive', 'Expressive and magnetic'],
    strengths: ['A commanding presence wherever you go', 'Never loses hope even in hard times', 'Charismatic leadership that draws people'],
    weaknesses: ['Hasty and impulsive decisions', 'Too much pride to admit mistakes', 'Intensity can overwhelm those nearby'],
    career: ['Entertainment, broadcasting, performance', 'Sales, marketing, PR', 'CEO, executive, politics'],
    relationship: 'Wants fun-to-be-with partners. Overly quiet types may feel boring.',
    wealth: 'Can make or lose big. Seek professional advice.',
    health: 'Watch your heart and blood pressure.',
    advice: 'Your passion is precious. But sometimes turn down the heat and look around.',
  },
  '丙申': {
    stem: '丙', branch: '申',
    title: 'Reflected Sunlight — Sunlight reflected on metal surfaces',
    summary: 'Sunlight bouncing off metal and shining even brighter — a unique combination of analytical ability and passion.',
    personality: ['Coexistence of analysis and passion', 'Sharp insight', 'Strong action and execution'],
    strengths: ['Logical yet passionate', 'Works systematically toward goals', 'Masters any field quickly'],
    weaknesses: ['Too direct, may unintentionally hurt people', 'Pushes yourself hard with perfectionism', 'Can lack flexibility'],
    career: ['IT, engineering, analytics', 'Finance, investment, trading', 'Broadcasting technology and direction'],
    relationship: 'Wants an intellectual and practical partner.',
    wealth: 'Creates stable returns through analysis-based investing.',
    health: 'Watch your respiratory system and heart.',
    advice: 'The balance of logic and passion is your greatest strength.',
  },
  '丙戌': {
    stem: '丙', branch: '戌',
    title: 'Hidden Ember — A spark hidden deep in rock',
    summary: 'Like an ember hidden inside rock — calm on the outside but burning with intense passion within.',
    personality: ['Calm outside, burning passion inside', 'Strong loyalty and trustworthiness', 'Once committed, goes all the way'],
    strengths: ['Trustworthy with steady momentum', 'Endures anything with resilience', 'Builds deep, lasting relationships'],
    weaknesses: ['Poor expression leads to misunderstandings', 'Tends to resist change', 'Stubbornness can cause clashes'],
    career: ['Military, police, civil service', 'Construction, real estate, manufacturing', 'Research and development'],
    relationship: 'Once they love, they devote themselves completely.',
    wealth: 'Builds solid assets through consistent accumulation.',
    health: 'Watch your stomach, joints, and skin.',
    advice: 'Your passion isn\'t a flash of fire — it\'s an eternal ember that never goes out.',
  },

  // ────── 丁(Ding/Yin Fire) ──────
  '丁丑': {
    stem: '丁', branch: '丑',
    title: 'Winter Candle — A small flame lit on frozen ground',
    summary: 'A candle flame in the middle of cold winter, illuminating the dark — strong will and a devoted spirit.',
    personality: ['Devoted and highly responsible', 'Patient and hardworking', 'Carries a firm will within'],
    strengths: ['Willpower to overcome any hardship', 'Warmly takes care of those around you', 'Becomes someone deeply trusted'],
    weaknesses: ['Tendency to over-sacrifice yourself', 'Lack of expression leads to misunderstandings', 'May resist change'],
    career: ['Healthcare, welfare, volunteering', 'Education and counseling', 'Civil service and stable careers'],
    relationship: 'Wants long-lasting, stable relationships.',
    wealth: 'Builds stable assets through consistent saving.',
    health: 'Watch your circulation and digestive system.',
    advice: 'Cherish time for yourself too.',
  },
  '丁卯': {
    stem: '丁', branch: '卯',
    title: 'Spring Warmth — The gentle warmth of a spring day',
    summary: 'The warm energy of a spring day that melts everything around it — rich sensitivity with brilliant artistic talent.',
    personality: ['Rich sensitivity and artistic nature', 'Warm and approachable', 'Inner flame expressed through creativity'],
    strengths: ['Outstanding talent in art and writing', 'Warms the hearts of others', 'Thoughtful and deeply considerate'],
    weaknesses: ['Easily swayed by emotions', 'May lack decisiveness', 'Can be easily hurt'],
    career: ['Art, literature, music', 'Design and craft', 'Counseling and education'],
    relationship: 'Wants a partner who connects deeply on an emotional level.',
    wealth: 'Monetizing artistic talent can generate significant income.',
    health: 'Watch your throat and eyes.',
    advice: 'Your sensitivity makes the world more beautiful.',
  },
  '丁巳': {
    stem: '丁', branch: '巳',
    title: 'Wisdom Flame — A flame of wisdom and intelligence',
    summary: 'The energy of a brilliant flame lighting the path of wisdom — intelligent, academic, and loves deep contemplation.',
    personality: ['Intelligent with strong intellectual curiosity', 'Cool and analytical thinking', 'Unique personality with natural charisma'],
    strengths: ['Excellent academic and professional ability', 'Piercing insight into the heart of problems', 'Logical and systematic thinking'],
    weaknesses: ['Strong pride can limit flexibility', 'Weak at expressing emotions', 'Perfectionism may lead to judging others'],
    career: ['Research, academia, professional fields', 'Law and medicine', 'IT and data analysis'],
    relationship: 'Forms deep connections with intellectually matched partners.',
    wealth: 'Income from professional expertise suits you best.',
    health: 'Watch your heart and nervous system.',
    advice: 'Knowledge and wisdom are not the same thing.',
  },
  '丁未': {
    stem: '丁', branch: '未',
    title: 'Sunset Glow — The crimson evening sky of summer',
    summary: 'The energy of a sunset painting the summer sky — beautiful, emotional, with brilliant artistic sensitivity.',
    personality: ['Artistic and emotional personality', 'Warm and deeply caring', 'Exceptional aesthetic sense'],
    strengths: ['Outstanding artistic sense', 'Ability to warmly embrace others', 'Keen observation skills'],
    weaknesses: ['Can be indecisive', 'Emotional swings', 'Sensitive to criticism'],
    career: ['Art, design, craft', 'Education and counseling', 'Cooking and floristry'],
    relationship: 'Wants a warm and emotionally rich partner.',
    wealth: 'Connecting your artistry to a career creates stable income.',
    health: 'Watch your digestive system and circulation.',
    advice: 'Your artistic sensitivity brings warmth to the world.',
  },
  '丁酉': {
    stem: '丁', branch: '酉',
    title: 'Glowing Candle — A gently glowing candlelight',
    summary: 'Soft and warm like candlelight — quiet on the outside but burning hot with passion within.',
    personality: ['Delicate and sensitive sensibility', 'Passionate inside, calm outside', 'Perfectionist tendencies'],
    strengths: ['Master of detail and precision', 'Ability to dig deep into one field', 'A trustworthy supporter'],
    weaknesses: ['Too sensitive, easily hurt', 'Pushes yourself too hard', 'Holding back leads to misunderstandings'],
    career: ['Research, development, professional fields', 'Design, art, music composition', 'Accounting, taxation, law'],
    relationship: 'Wants a considerate partner who pays attention to details.',
    wealth: 'Can become wealthy through consistent accumulation.',
    health: 'Watch your eyes, heart, and circulation.',
    advice: 'You don\'t need to be perfect. You\'re already shining just as you are.',
  },
  '丁亥': {
    stem: '丁', branch: '亥',
    title: 'Ember in Water — A spark that refuses to die underwater',
    summary: 'An ember that won\'t be extinguished even underwater — possessing a resilient inner strength that overcomes any difficulty.',
    personality: ['Strong inner core and unbreakable will', 'Strong intuition with mysterious charm', 'Emotional yet unbreakable'],
    strengths: ['Inner strength to endure any hardship', 'Deep intuition and insight', 'Draws people with unique charm'],
    weaknesses: ['Suppressing emotions can lead to an explosion', 'Enjoys solitude but sometimes feels lonely', 'May struggle with compromise'],
    career: ['Psychology, counseling, healing', 'Art and literature', 'Research and exploration'],
    relationship: 'Craves deep spiritual connection.',
    wealth: 'Builds wealth gradually with a stable job as foundation.',
    health: 'Watch your kidneys and heart.',
    advice: 'The flame that won\'t go out even underwater — that\'s a miracle.',
  },

  // ────── 戊(Wu/Yang Earth) ──────
  '戊子': {
    stem: '戊', branch: '子',
    title: 'Mountain with Water — A great mountain holding water',
    summary: 'A solid mountain holding water within — reliable and all-embracing, tough outside but gentle and wise within.',
    personality: ['Reliable and trustworthy', 'Broad tolerance and wisdom', 'Practical and realistic'],
    strengths: ['Provides calm stability in any situation', 'Puts others at ease through tolerance', 'Outstanding realistic judgment'],
    weaknesses: ['Poor at expressing emotions', 'Fears change', 'Can lack flexibility'],
    career: ['Finance, banking, insurance', 'Real estate and construction', 'Civil service and large corporations'],
    relationship: 'Finds lifelong happiness with a trustworthy partner.',
    wealth: 'Suited for real estate and safe asset investment.',
    health: 'Watch your kidneys and bladder.',
    advice: 'Your dependability is the pillar everyone around you leans on.',
  },
  '戊寅': {
    stem: '戊', branch: '寅',
    title: 'Mountain Forest — A great mountain holding a forest',
    summary: 'A mountain holding a lush forest — abundant and overflowing with vitality, combining leadership and tolerance.',
    personality: ['Strong leadership with broad tolerance', 'Action-oriented with decisive energy', 'Extremely strong sense of responsibility'],
    strengths: ['Leadership ability to guide a team', 'Takes on any challenge head-on', 'Reliable and loyal'],
    weaknesses: ['Strong assertions can cause clashes', 'May ignore others\' opinions', 'Burnout can set in without rest'],
    career: ['Entrepreneurship and senior management', 'Military, police, public institutions', 'Sports and coaching'],
    relationship: 'Wants a partnership where you grow together.',
    wealth: 'Business ability can create great wealth.',
    health: 'Watch your liver and muscles.',
    advice: 'Adding gentleness to your strength makes you an even greater leader.',
  },
  '戊辰': {
    stem: '戊', branch: '辰',
    title: 'Great Mountain — A solid and immovable mountain',
    summary: 'Solid and steady like a great mountain — reliable with a strong sense of responsibility.',
    personality: ['Trustworthy and reliable', 'Practical and realistic', 'Strong perseverance and endurance'],
    strengths: ['Always sees commitments through to the end', 'Sharp financial sense', 'Stays unshaken even in crisis'],
    weaknesses: ['Lacks flexibility and can be stubborn', 'Resists change', 'Poor expression can cause misunderstandings'],
    career: ['Real estate, construction, civil engineering', 'Finance, banking, insurance', 'Civil service and large corporations'],
    relationship: 'Finds lifelong happiness with a trustworthy partner.',
    wealth: 'Good fortune in real estate. Steady accumulation builds great assets.',
    health: 'Watch your stomach and digestive system.',
    advice: 'Your reliability is a great asset. Try being more flexible sometimes.',
  },
  '戊午': {
    stem: '戊', branch: '午',
    title: 'Summer Mountain — A mountain heated by the summer sun',
    summary: 'A mountain scorched intensely by the summer sun — carrying fierce energy within.',
    personality: ['Intense energy and charisma', 'Proactive and dominant personality', 'Strong passion and conviction'],
    strengths: ['Strong drive and execution', 'Outstanding leadership', 'Never afraid of any challenge'],
    weaknesses: ['May clash with others when overheated', 'Needs emotional regulation', 'Can make unilateral decisions'],
    career: ['Entrepreneur and manager', 'Politics and leadership', 'Military, police, firefighting'],
    relationship: 'Needs a strong partner who understands you.',
    wealth: 'Sharp business intuition can create great wealth.',
    health: 'Watch your heart and blood pressure.',
    advice: 'Adding wisdom\'s water to a burning heart makes you perfect.',
  },
  '戊申': {
    stem: '戊', branch: '申',
    title: 'Mineral Mountain — A mountain rich in metals',
    summary: 'Like a mineral-rich mountain — possessing abundant internal resources. Practical and highly capable.',
    personality: ['Practical and capability-driven personality', 'Sharp judgment and analytical mind', 'Excellent sense of reality'],
    strengths: ['Efficiently utilizes diverse talents and resources', 'Goal-oriented and results-driven', 'Outstanding problem-solving ability'],
    weaknesses: ['Too pragmatic, lacks emotional exchange', 'May prioritize self-interest', 'Chasing perfect conditions can miss opportunities'],
    career: ['Mining, resources, natural materials', 'Finance and investment', 'IT and engineering'],
    relationship: 'Wants a practical and capable partner.',
    wealth: 'Builds wealth through efficient use of resources.',
    health: 'Watch your respiratory system and large intestine.',
    advice: 'Share your abundant talents with the world.',
  },
  '戊戌': {
    stem: '戊', branch: '戌',
    title: 'Rugged Mountain — A steep and majestic autumn mountain',
    summary: 'Like a steep and grand mountain — strong will with broad tolerance.',
    personality: ['Strong will and principled character', 'Deep loyalty and responsibility', 'Strong pride and independence'],
    strengths: ['Firmly stands through any situation', 'Earns respect and goodwill through trust and loyalty', 'Embraces others with broad tolerance'],
    weaknesses: ['Stubbornness makes compromise difficult', 'Pride prevents asking for help', 'Can lack flexibility'],
    career: ['Civil service, military, police', 'Large corporations and management', 'Construction and real estate'],
    relationship: 'Wants a partner who respects you.',
    wealth: 'Builds assets through stable employment and consistent saving.',
    health: 'Watch your stomach and joints.',
    advice: 'A mountain stands alone yet holds many things. So do you.',
  },

  // ────── 己(Ji/Yin Earth) ──────
  '己丑': {
    stem: '己', branch: '丑',
    title: 'Rain-hardened Soil — Ground that solidifies after the rain',
    summary: 'The energy of ground that hardens after rain. Strong endurance and patience, ultimately blooming with results.',
    personality: ['Remarkable endurance and persistence', 'Careful and conservative', 'Firm and strong inner will'],
    strengths: ['Life force to endure any hardship', 'Excellent financial awareness', 'Reliable partner'],
    weaknesses: ['Lacks flexibility', 'May cling to the past', 'Difficult to embrace change'],
    career: ['Agriculture, livestock, real estate', 'Distribution, wholesale, trade', 'Civil service and accounting'],
    relationship: 'Reliable partners are the best match.',
    wealth: 'Can become wealthy through consistent accumulation.',
    health: 'Watch your stomach and joints.',
    advice: 'Your patience is a great asset.',
  },
  '己卯': {
    stem: '己', branch: '卯',
    title: 'Garden Soil — Rich earth of a flower garden',
    summary: 'The energy of rich soil that makes flowers bloom abundantly. Large tolerance and the gift of making surroundings beautiful.',
    personality: ['Broad tolerance and gentle personality', 'Rich sensitivity with artistic nature', 'Holds both dreams and reality'],
    strengths: ['Makes surroundings comfortable and beautiful', 'Deep patience and consideration', 'Practical creativity'],
    weaknesses: ['May lack decisiveness', 'Has indecisive moments', 'Emotions can cloud judgment'],
    career: ['Art, horticulture, interior', 'Education, childcare, welfare', 'Food and service industries'],
    relationship: 'Wants a warm, considerate partner.',
    wealth: 'Creates stable income through steady effort.',
    health: 'Watch your digestive system and immunity.',
    advice: 'In the beautiful environment you create, live happily yourself too.',
  },
  '己巳': {
    stem: '己', branch: '巳',
    title: 'Fire-forged Earth — Ground tempered by fire',
    summary: 'The energy of earth made stronger by fire — intelligent, action-oriented, and results-driven.',
    personality: ['Intelligent and practical personality', 'Results-oriented with strong drive', 'Sociable and highly adaptable'],
    strengths: ['Outstanding goal achievement ability', 'Adapts quickly to various situations', 'Good at realizing practical ideas'],
    weaknesses: ['Can over-prioritize personal interest', 'Obsessed with results over process', 'Can make impulsive decisions'],
    career: ['Business and marketing', 'Real estate and distribution', 'Consulting and sales'],
    relationship: 'Wants a realistic partner who shares similar values.',
    wealth: 'Sharp business sense brings good fortune in wealth.',
    health: 'Watch your heart and digestive system.',
    advice: 'Results matter, but don\'t miss the joy in the process.',
  },
  '己未': {
    stem: '己', branch: '未',
    title: 'Fertile Land — Rich earth moistened by summer rain',
    summary: 'The energy of fertile land promising abundant harvest — broad tolerance and someone who puts others at ease.',
    personality: ['Broad tolerance and warm character', 'Combines artistic sensibility with practical sense', 'Friendly with anyone'],
    strengths: ['Earns trust through warm consideration', 'Tolerance that embraces diverse people', 'Consistent and hardworking sincerity'],
    weaknesses: ['May exhaust yourself by prioritizing others', 'Can become indecisive', 'Easily led by emotions'],
    career: ['Welfare and social services', 'Education and counseling', 'Agriculture and food'],
    relationship: 'Gets along with anyone but takes time to find the right match.',
    wealth: 'Stable saving and modest investing suits you.',
    health: 'Watch your digestive system and joints.',
    advice: 'The more you share your abundance, the more comes back to you.',
  },
  '己酉': {
    stem: '己', branch: '酉',
    title: 'Harvest Earth — Autumn fields ready for harvest',
    summary: 'The energy of autumn fields welcoming a rich harvest — meticulous, sincere, and values completeness.',
    personality: ['Meticulous and perfection-seeking', 'Practical and realistic', 'Values tradition and principle'],
    strengths: ['High quality of work and thorough execution', 'Excellent financial sense', 'Projects a trustworthy and stable image'],
    weaknesses: ['Demands too much perfection from others', 'Can be slow to adapt to change', 'Conservative thinking may cause you to miss opportunities'],
    career: ['Taxation, accounting, law', 'Quality control and inspection', 'Civil service and finance'],
    relationship: 'Wants a trustworthy and stable partner.',
    wealth: 'Builds solid assets through consistent saving.',
    health: 'Watch your lungs and large intestine.',
    advice: 'Completion matters more than perfection. Sometimes 80% is enough.',
  },
  '己亥': {
    stem: '己', branch: '亥',
    title: 'Water-filled Paddies — Paddies full of nourishing water',
    summary: 'Like paddies brimming with water — abundant and productive energy. Wise and all-embracing.',
    personality: ['Wise with strong intuition', 'Broad tolerance and accepting nature', 'Enjoys deep inner contemplation'],
    strengths: ['Wise judgment and intuition', 'High empathy through rich sensitivity', 'Steady and stable effort'],
    weaknesses: ['Can be indecisive', 'Accepting too much can lead to exhaustion', 'Action-oriented thinking may be lacking'],
    career: ['Counseling and psychotherapy', 'Education and research', 'Trade and distribution'],
    relationship: 'Wants a warm partner with whom deep connection is possible.',
    wealth: 'Steady accumulation creates stable assets.',
    health: 'Watch your kidneys and digestive system.',
    advice: 'Trust your own wise judgment more.',
  },

  // ────── 庚(Geng/Yang Metal) ──────
  '庚子': {
    stem: '庚', branch: '子',
    title: 'Sword in Water — A sharp blade submerged in cold water',
    summary: 'Like a sharp sword immersed in cold water — cool analytical ability and formidable strength.',
    personality: ['Cool and analytical thinking', 'Strong will and decisiveness', 'Values principles'],
    strengths: ['Clear and logical judgment', 'Resilience to break through any difficulty', 'Powerful focus on goals'],
    weaknesses: ['Can seem cold, making relationships difficult', 'Poor at expressing emotions', 'Too direct can hurt others'],
    career: ['Law, medicine, engineering', 'Finance and analytics', 'Research and development'],
    relationship: 'Appears cool but is warm inside. Practice expressing feelings.',
    wealth: 'Creates stable returns through analytical investing.',
    health: 'Watch your kidneys and bladder.',
    advice: 'Show the world the warmth hiding behind your cool exterior.',
  },
  '庚寅': {
    stem: '庚', branch: '寅',
    title: 'Mountain Axe — An axe that can fell mountains',
    summary: 'The powerful energy of an axe that seems capable of felling mountains. Strong will overcomes any obstacle.',
    personality: ['Tough and challenge-driven', 'Strong drive and action', 'High competitive spirit'],
    strengths: ['Never afraid of any challenge', 'Strong leadership and decisiveness', 'Always achieves set goals'],
    weaknesses: ['Frequent clashes with others', 'Can lack patience', 'Poor at considering those nearby'],
    career: ['Entrepreneurship and business', 'Military, police, firefighting', 'Sports and athletics'],
    relationship: 'Attracted to strong matches but conflicts are frequent.',
    wealth: 'Aggressive business activity can generate significant profit.',
    health: 'Watch your liver and muscles.',
    advice: 'Strength is beautiful. Cultivate gentleness alongside it.',
  },
  '庚辰': {
    stem: '庚', branch: '辰',
    title: 'Raw Ore — Unrefined ore freshly mined from the mountain',
    summary: 'Like newly mined ore — possessing great untapped potential that gradually emerges.',
    personality: ['Great potential and strong adaptability', 'Practical and realistic', 'Strong ego and drive'],
    strengths: ['Growth-oriented as hidden abilities emerge', 'Strong execution power', 'Combines tolerance and leadership'],
    weaknesses: ['Can develop arrogance', 'Emotional processing can feel awkward', 'May seem stubborn'],
    career: ['Manufacturing, technology, engineering', 'Entrepreneurs and business people', 'Real estate and construction'],
    relationship: 'Wants a partner who recognizes your worth.',
    wealth: 'Steady polishing creates great wealth.',
    health: 'Watch your digestive system and joints.',
    advice: 'Even raw ore becomes a gemstone when polished. Keep growing.',
  },
  '庚午': {
    stem: '庚', branch: '午',
    title: 'Tempered Sword — A blade refined in fire',
    summary: 'Like a sword forged in flames — resilient and brilliant. Every trial makes you stronger.',
    personality: ['Strong charisma that shines', 'Gets stronger through trials', 'Decisive and overflowing with confidence'],
    strengths: ['Turns any adversity into a stepping stone', 'Charismatic leadership', 'Strong drive and execution'],
    weaknesses: ['May overwhelm surroundings with too much force', 'Strong pride makes compromise difficult', 'Can overheat'],
    career: ['CEO and management', 'Military, police, law', 'Athletes and coaches'],
    relationship: 'Wants an equal partnership.',
    wealth: 'Can achieve great results from bold business or investment.',
    health: 'Watch your heart and blood pressure.',
    advice: 'The more you\'re forged, the brighter you shine. That\'s you.',
  },
  '庚申': {
    stem: '庚', branch: '申',
    title: 'Gemstone — A hard, sharp, gleaming jewel',
    summary: 'Like pomegranate seeds — hard and sparkling. Strong drive and decisiveness.',
    personality: ['Loyal with strong decisiveness', 'Strong sense of justice and directness', 'Competitive and enjoys competition'],
    strengths: ['Power to charge straight at goals', 'Resilience that enjoys difficulty', 'Leadership and drive'],
    weaknesses: ['Sharpness can hurt others', 'Stubborn with little flexibility', 'Prone to burnout'],
    career: ['Law, police, military', 'Surgery and dentistry', 'Finance, investment, business'],
    relationship: 'Wants someone who accepts you as you are.',
    wealth: 'Has the fortune to earn big. Be careful with risky investments.',
    health: 'Watch your respiratory system and lungs.',
    advice: 'Your strength is admirable. Being gentle sometimes is okay too.',
  },
  '庚戌': {
    stem: '庚', branch: '戌',
    title: 'Stored Metal — Accumulated metal reserves, powerful and latent',
    summary: 'Like stockpiled metal — accumulated strength and knowledge that explodes dramatically when the time comes.',
    personality: ['Careful and patient, biding time', 'Strong will with conservative nature', 'Deep loyalty and responsibility'],
    strengths: ['Thorough execution when prepared', 'Strong endurance and will', 'Stable and trustworthy character'],
    weaknesses: ['Waiting too long can miss opportunities', 'Conservative attitude toward new things', 'Can seem stubborn'],
    career: ['Finance, insurance, asset management', 'Law and civil service', 'Manufacturing and technical fields'],
    relationship: 'Wants a stable and trustworthy partner.',
    wealth: 'Builds great assets through careful investment and consistent saving.',
    health: 'Watch your lungs, large intestine, and joints.',
    advice: 'Waiting is a strategy. But waiting forever gets nothing done.',
  },

  // ────── 辛(Xin/Yin Metal) ──────
  '辛丑': {
    stem: '辛', branch: '丑',
    title: 'Buried Gem — A jewel hidden in frozen earth',
    summary: 'Like a gemstone buried in frozen ground — not outwardly visible, but possessing brilliance inside.',
    personality: ['Introverted but internally radiant', 'Meticulous and careful character', 'Has a distinct inner world'],
    strengths: ['Delicate and precise ability', 'Unique and deep talent', 'Creates results through consistent effort'],
    weaknesses: ['Lack of expression may bury talent', 'Strong pride can make collaboration difficult', 'Can explode after long patience'],
    career: ['Art, craft, jewelry design', 'Research and development', 'Taxation and accounting'],
    relationship: 'Wants a partner who deeply understands you.',
    wealth: 'Creates stable income through professional skills.',
    health: 'Watch your digestive system and joints.',
    advice: 'Your light will surely reach the world.',
  },
  '辛卯': {
    stem: '辛', branch: '卯',
    title: 'Spring Jewel — A gemstone sparkling in spring light',
    summary: 'A gemstone sparkling in spring light — delicate and beautiful with abundant sensitivity and talent.',
    personality: ['Rich sensitivity and artistic nature', 'Delicate, beauty-seeking temperament', 'Sociable with good expression'],
    strengths: ['Outstanding artistic talent', 'Draws people with warm sensitivity', 'Flexible and creative thinking'],
    weaknesses: ['Strong pride needs recognition', 'Can be fickle with scattered focus', 'Sensitive to criticism'],
    career: ['Fashion, beauty, arts', 'Broadcasting and entertainment', 'Education and counseling'],
    relationship: 'Wants an emotionally compatible partner.',
    wealth: 'Monetizing your talent generates significant income.',
    health: 'Watch your throat and eyes.',
    advice: 'Like a spring gemstone, you make the world beautiful.',
  },
  '辛巳': {
    stem: '辛', branch: '巳',
    title: 'Pearl in Flames — A pearl that shines even in fire',
    summary: 'Like a pearl that never loses its luster even in fire — brilliant through any trial. Intelligent and magnetic.',
    personality: ['Intelligent and magnetic', 'Shines brighter through trials', 'Intellectual and logical'],
    strengths: ['Outstanding intellectual ability and insight', 'Turns every trial into an opportunity for growth', 'Draws attention with unique appeal'],
    weaknesses: ['Can develop arrogance', 'Emotional expression feels awkward', 'Perfectionism causes stress'],
    career: ['Research and academia', 'Law and medicine', 'IT and data'],
    relationship: 'Wants an intellectually equal partner.',
    wealth: 'Expertise creates stable high income.',
    health: 'Watch your heart and eyes.',
    advice: 'Your light grows stronger through trials.',
  },
  '辛未': {
    stem: '辛', branch: '未',
    title: 'Field Gold — Gold sparkling in open fields',
    summary: 'Like gold glittering across wide fields — free and beautiful energy with artistic sensibility and tolerance.',
    personality: ['Free-spirited and artistic nature', 'Warm and tolerant character', 'Delicate sensibility'],
    strengths: ['Outstanding artistic talent', 'Warm consideration that brings people together', 'Creative ideas'],
    weaknesses: ['May lack decisiveness', 'Emotions can cloud judgment', 'Weak self-assertion'],
    career: ['Art, music, craft', 'Education and volunteering', 'Fashion and interior'],
    relationship: 'Wants warm and free-flowing relationships.',
    wealth: 'Artistic talent creates consistent income.',
    health: 'Watch your digestive system and lungs.',
    advice: 'Shine freely like gold across open fields.',
  },
  '辛酉': {
    stem: '辛', branch: '酉',
    title: 'Refined Jewel — A precisely cut and polished gemstone',
    summary: 'Like a precisely refined jewel — pursues sophistication and perfection in all things.',
    personality: ['Perfectionist tendencies', 'Delicate and sensitive sensibility', 'Loves cleanliness and order'],
    strengths: ['Master of detail and precision', 'Outstanding artistic sense', 'Trusted professional'],
    weaknesses: ['Too sensitive, easily hurt', 'Exhausted by pursuit of perfection', 'Weak to criticism'],
    career: ['Jewelry, fashion, design', 'Art, music, fine arts', 'Tax, accounting, prosecution'],
    relationship: 'Wants a considerate partner with detailed attention.',
    wealth: 'Leveraging expertise can make big money.',
    health: 'Watch your lungs, respiratory system, and skin.',
    advice: 'You don\'t need to be perfect. You\'re already beautiful enough.',
  },
  '辛亥': {
    stem: '辛', branch: '亥',
    title: 'Sunken Gem — A jewel submerged in deep water',
    summary: 'Like a gemstone resting in deep water — mysterious inner world with deep emotional sensitivity.',
    personality: ['Mysterious and introverted charm', 'Strong intuition and rich sensitivity', 'Unique personality and worldview'],
    strengths: ['Deep intuition and insight', 'Draws people with unique appeal', 'Delicate and accurate sense'],
    weaknesses: ['Loving solitude can lead to loneliness', 'Emotional fluctuations', 'May struggle with compromise'],
    career: ['Research and exploration', 'Art and literature', 'Psychology and counseling'],
    relationship: 'Wants a partner who connects spiritually and deeply.',
    wealth: 'Creates income through unique and original methods.',
    health: 'Watch your kidneys and lungs.',
    advice: 'Like a gem hidden in the depths, show your true value to the world.',
  },

  // ────── 壬(Ren/Yang Water) ──────
  '壬子': {
    stem: '壬', branch: '子',
    title: 'Great River — A mighty river flowing without restraint',
    summary: 'The energy of a great river flowing freely — free-spirited, open, and loves change.',
    personality: ['Free soul', 'Open and sociable', 'Loves change and adventure'],
    strengths: ['Adapts anywhere', 'Creative and flexible thinking', 'Gets along well with people'],
    weaknesses: ['Fickle with weak persistence', 'Acts impulsively without planning', 'Can appear irresponsible'],
    career: ['Trade, distribution, logistics', 'Travel, tourism, hospitality', 'IT, startups, media'],
    relationship: 'Wants a partner who can adventure together.',
    wealth: 'Money comes in as fast as it goes out. Build saving habits.',
    health: 'Watch your kidneys, bladder, and ears.',
    advice: 'Your freedom is charming. But sometimes stop and look around you.',
  },
  '壬寅': {
    stem: '壬', branch: '寅',
    title: 'Spring Source — Water surging powerfully from a mountain spring',
    summary: 'The energy of a spring surging forcefully from deep within a mountain — overflowing vitality and strong life force.',
    personality: ['Active and overflowing with vitality', 'Challenge-seeking and progressive', 'Creative and independent temperament'],
    strengths: ['Never fears anything new', 'Strong vitality and recovery', 'Creative problem-solving ability'],
    weaknesses: ['Impulsiveness needs follow-up', 'Can have scattered focus', 'Emotional fluctuations'],
    career: ['Exploration and adventure industries', 'Startups and entrepreneurship', 'Sports and athletics'],
    relationship: 'Wants an active and free partner.',
    wealth: 'Business activity can generate significant income.',
    health: 'Watch your liver and muscles.',
    advice: 'Concentrating your surging energy in one direction creates tremendous results.',
  },
  '壬辰': {
    stem: '壬', branch: '辰',
    title: 'Dragon Lake — A deep lake where the dragon plays',
    summary: 'Like a mysterious lake where dragons dwell — deep and vast energy. Outstanding wisdom and tolerance.',
    personality: ['Wise with broad tolerance', 'Mysterious charm and deep insight', 'Free yet responsible'],
    strengths: ['Deep insight and wisdom', 'Broad knowledge and tolerance', 'Handles any situation flexibly'],
    weaknesses: ['Decisions take time', 'Trying to hold too much can lead to fatigue', 'Can become scattered'],
    career: ['Research and academia', 'Diplomacy and international relations', 'IT and data analysis'],
    relationship: 'Wants a partner with whom deep intellectual exchange is possible.',
    wealth: 'Has the talent to generate income from diverse fields.',
    health: 'Watch your digestive system and kidneys.',
    advice: 'Sharing your deep wisdom with the world makes you shine even brighter.',
  },
  '壬午': {
    stem: '壬', branch: '午',
    title: 'Sun-lit River — A river reflecting intense sunlight',
    summary: 'A river gleaming intensely under sunlight — passion and wisdom coexist in a unique and magnetic way.',
    personality: ['Where passion and wisdom coexist', 'Emotional yet rational', 'Active with strong expressiveness'],
    strengths: ['Harmoniously expresses diverse abilities', 'Passionate yet cool-headed judgment', 'An inspiring presence for others'],
    weaknesses: ['Opposing temperaments create inner conflict', 'Emotional fluctuations', 'Can exhaust energy from overexertion'],
    career: ['Broadcasting and media', 'Education and speaking', 'Business and marketing'],
    relationship: 'Wants a partner who is both intellectual and emotional.',
    wealth: 'Has the talent to create multiple income streams.',
    health: 'Watch your heart and kidneys.',
    advice: 'When fire and water meet, they become steam and rise even higher.',
  },
  '壬申': {
    stem: '壬', branch: '申',
    title: 'Rock Spring — A clear spring surging through rock crevices',
    summary: 'The energy of clear spring water surging through rock crevices — sharp insight and practical, analytical mind.',
    personality: ['Sharp and analytical mind', 'Practical and goal-oriented', 'Cool judgment'],
    strengths: ['Outstanding analytical and logical ability', 'Practical and efficient problem-solving', 'Fast learning ability'],
    weaknesses: ['Too pragmatic, appears emotionally cold', 'Can seem cold to others', 'Competitive spirit creates friction'],
    career: ['IT and engineering', 'Finance and consulting', 'Research and analysis'],
    relationship: 'Wants an intellectual and practical partner.',
    wealth: 'Creates stable income through analytical investing and business.',
    health: 'Watch your respiratory system and kidneys.',
    advice: 'Adding warmth to sharpness makes a perfect leader.',
  },
  '壬戌': {
    stem: '壬', branch: '戌',
    title: 'Stored Water — Mighty water stored behind a dam',
    summary: 'Like an enormous volume of water stored in a dam — carrying tremendous inner power.',
    personality: ['Strong inner power and deep thoughts', 'Careful and patient', 'Strong loyalty and responsibility'],
    strengths: ['Releases power at just the right moment', 'Trustworthy and loyal', 'Strong endurance'],
    weaknesses: ['Too much patience can lead to explosion', 'Conservative attitude may miss opportunities', 'Poor at expressing emotions'],
    career: ['Finance and asset management', 'Civil service and public enterprises', 'Research and development'],
    relationship: 'Wants stable and deep relationships.',
    wealth: 'Builds solid assets through careful investment.',
    health: 'Watch your kidneys and stomach.',
    advice: 'Use the stored power explosively at the right moment.',
  },

  // ────── 癸(Gui/Yin Water) ──────
  '癸丑': {
    stem: '癸', branch: '丑',
    title: 'Winter Snow — Snow that has fallen on frozen winter earth',
    summary: 'Like snow covering winter ground — quiet and still but destined to nourish the whole world when spring comes.',
    personality: ['Quiet and highly patient', 'Has a deep inner world', 'Broad tolerance and warmth'],
    strengths: ['Patience to endure any situation', 'Melts those around you with tolerance', 'Creates results through consistent effort'],
    weaknesses: ['Hard to express your own opinions', 'Enduring too much can lead to exhaustion', 'Can become indecisive'],
    career: ['Education, counseling, welfare', 'Research and academia', 'Civil service and stable employment'],
    relationship: 'Wants a warm, understanding partner.',
    wealth: 'Consistent saving builds stable assets.',
    health: 'Watch your kidneys and digestive system.',
    advice: 'When spring comes, all snow melts and enriches the world.',
  },
  '癸卯': {
    stem: '癸', branch: '卯',
    title: 'Spring Rain — A gentle spring shower',
    summary: 'Like a life-giving spring rain — energy that breathes life and vitality into the surroundings. Rich in sensitivity and creativity.',
    personality: ['Rich sensitivity and creative nature', 'Warm and friendly personality', 'Delicate and artistic temperament'],
    strengths: ['Outstanding artistic and creative talent', 'Breathes vitality into those around you', 'High empathy and emotional intelligence'],
    weaknesses: ['Easily swayed by emotions', 'May lack decisiveness', 'Sensitive to criticism'],
    career: ['Art, literature, music', 'Education and counseling', 'Design and media'],
    relationship: 'Wants a partner who connects deeply on an emotional level.',
    wealth: 'Monetizing creative talent creates income.',
    health: 'Watch your throat and immune system.',
    advice: 'Your sensitivity breathes spring rain-like life into the world.',
  },
  '癸巳': {
    stem: '癸', branch: '巳',
    title: 'Rising Steam — Steam rising from water over intense fire',
    summary: 'Like steam evaporating over fire — a unique mix of passion and wisdom. Variable and creative.',
    personality: ['Creative and change-loving personality', 'Intelligent with strong adaptability', 'Coexistence of intuition and passion'],
    strengths: ['Quick adaptability to situations', 'Creative problem-solving', 'Versatile and multi-talented'],
    weaknesses: ['Focus may scatter', 'Can make impulsive decisions', 'Emotional fluctuations'],
    career: ['IT and technical fields', 'Media and broadcasting', 'Consulting and planning'],
    relationship: 'Attracted to partners with diverse charm.',
    wealth: 'Has the ability to create income from diverse fields.',
    health: 'Watch your heart and kidneys.',
    advice: 'The hotter the steam rises, the higher it goes. That\'s you.',
  },
  '癸未': {
    stem: '癸', branch: '未',
    title: 'Nurturing Rain — Rainwater soaking into the earth',
    summary: 'The energy of rain gently moistening parched land — broad tolerance, deep consideration, and soft charm.',
    personality: ['Broad tolerance and warm character', 'Emotional with deep consideration', 'Artistic sensibility'],
    strengths: ['Ability to make others feel comfortable', 'Deep empathy', 'Gentle yet consistent effort'],
    weaknesses: ['Self-sacrifice can lead to exhaustion', 'May lack decisiveness', 'Emotional fluctuations'],
    career: ['Counseling, healing, welfare', 'Art and music', 'Education and service'],
    relationship: 'Wants warm and stable relationships.',
    wealth: 'Consistent effort creates stable assets.',
    health: 'Watch your digestive system and joints.',
    advice: 'Like steady rain, you continuously enrich those around you.',
  },
  '癸酉': {
    stem: '癸', branch: '酉',
    title: 'Clear Water Jewel — Pure water washing a precious gem',
    summary: 'Like spring water washing a jewel clean — pure and delicate energy. Sensitive nature with refined aesthetic.',
    personality: ['Pure and delicate sensibility', 'High aesthetic sense and artistic nature', 'Introverted with deep inner world'],
    strengths: ['Outstanding artistic sensibility', 'Careful and precise observation', 'Deep professional expertise'],
    weaknesses: ['Too sensitive, easily hurt', 'Perfectionism causes stress', 'Self-expression is difficult'],
    career: ['Art, fine arts, music', 'Fashion and beauty', 'Research and professional fields'],
    relationship: 'Wants a partner who understands you on a subtle level.',
    wealth: 'Earns income through professional artistic talent.',
    health: 'Watch your lungs and kidneys.',
    advice: 'Your purity makes the world shine.',
  },
  '癸亥': {
    stem: '癸', branch: '亥',
    title: 'Deep Ocean — A vast and boundless sea',
    summary: 'Like a deep and vast ocean — great tolerance and wisdom. Rich in sensitivity with profound understanding.',
    personality: ['Great tolerance with deep understanding', 'Rich sensitivity and strong intuition', 'Has a mysterious charm'],
    strengths: ['Ability to read people\'s hearts', 'Creative and artistic talent', 'Calmness in crisis situations'],
    weaknesses: ['Can become too emotional or depressed', 'May lose touch with reality', 'Weak sense of boundaries — can be taken advantage of'],
    career: ['Art, literature, film', 'Counseling, healing, religion', 'Ocean, aquaculture, travel'],
    relationship: 'Best with a partner capable of deep emotional exchange.',
    wealth: 'Can earn through artistic talent. Must build a stable income source.',
    health: 'Watch your kidneys, reproductive system, and immunity.',
    advice: 'Your depth is precious. Sometimes rise to the surface and bask in the sunlight.',
  },
};

const DEFAULT_EN: DayPillarInterpretation = {
  stem: '', branch: '',
  title: 'Balanced Four Pillars',
  summary: 'Your chart has a relatively balanced distribution of the Five Elements. More stable than extreme, with grounded and harmonious energy.',
  personality: ['Balanced and well-rounded nature', 'Adapts flexibly to situations', 'Not prone to extremes'],
  strengths: ['Trusted for your stable personality', 'Good adaptability to varied situations', 'Can navigate life smoothly and steadily'],
  weaknesses: ['May not stand out in any single area', 'Tends to overthink decisions', 'May settle for comfort rather than bold challenges'],
  career: ['General office work, civil service', 'Stable large corporations', 'Professional fields'],
  relationship: 'Easy-going nature means you connect well with many types of people.',
  wealth: 'Can build stable assets through consistent saving.',
  health: 'Generally healthy, but consistent daily routines are important.',
  advice: 'Your reliability is a great strength. Try embracing the occasional bold challenge.',
};

export function getDayPillarInterpretationEn(stem: string, branch: string): DayPillarInterpretation {
  const key = `${stem}${branch}`;
  return INTERPRETATIONS_EN[key] || DEFAULT_EN;
}
